import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import SuperTokens, { SuperTokensWrapper } from "supertokens-auth-react";
import Session from "supertokens-auth-react/recipe/session";
import EmailVerification from "supertokens-auth-react/recipe/emailverification";
import "../src/styles/globals.css";
import ThirdParty, { Google } from "supertokens-auth-react/recipe/thirdparty";
import EmailPassword from "supertokens-auth-react/recipe/emailpassword";
import { AuthRecipeComponentsOverrideContextProvider } from "supertokens-auth-react/ui/index.js";
import AuthPage from "./pages/AuthPage";
import { useEffect } from "react";
import { PostHogProvider } from "posthog-js/react";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://0e52b3391a8e9ce1add5f1b16f7ba482@o4508841353478144.ingest.us.sentry.io/4508841356165120",
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [import.meta.env.VITE_API_DOMAIN],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 6 * 60 * 60 * 1000, // 24 hours
      cacheTime: Infinity,
    },
  },
});

const CustomAuthComponents = {
  EmailPasswordSignInForm_Override: ({ DefaultComponent, ...props }) => {
    return <AuthPage isSignIn={true} />;
  },
  EmailPasswordSignUpForm_Override: ({ DefaultComponent, ...props }) => {
    return <AuthPage isSignIn={false} />;
  },
};

SuperTokens.init({
  appInfo: {
    appName: "SamSearch",
    apiDomain: import.meta.env.VITE_API_DOMAIN,
    websiteDomain: import.meta.env.VITE_DOMAIN,
    apiBasePath: "/auth",
    websiteBasePath: "/auth",
  },
  style: `
  [data-supertokens~=container] {
    --palette-primary:  2, 132, 199;
    --palette-primaryBorder: 14, 165, 233;
    border-radius: var(--border-radius-xlg);
    --palette-textLink: 2, 132, 199;
  }
    [data-supertokens~=superTokensBranding] {
               display: none;
  }
`,
  recipeList: [
    ThirdParty.init({
      signInAndUpFeature: {
        providers: [
          Google.init(),
          {
            id: "custom-linkedin",
            name: "Linkedin", // Will display "Continue with Linkedin"

            // optional
            // you do not need to add a click handler to this as
            // we add it for you automatically.
            buttonComponent: (props) => (
              <div data-supertokens="providerContainer">
                <span>
                  <button data-supertokens="button providerButton providerLinkedIn">
                    <div data-supertokens="providerButtonLeft">
                      <div data-supertokens="providerButtonLogo">
                        <div data-supertokens="providerButtonLogoCenter">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 48"
                            width="20"
                            height="20"
                          >
                            <path
                              fill="#0288D1"
                              d="M42,37c0,2.762-2.238,5-5,5H11c-2.761,0-5-2.238-5-5V11c0-2.762,2.239-5,5-5h26c2.762,0,5,2.238,5,5V37z"
                            ></path>
                            <path
                              fill="#FFF"
                              d="M12 19H17V36H12zM14.485 17h-.028C12.965 17 12 15.888 12 14.499 12 13.08 12.995 12 14.514 12c1.521 0 2.458 1.08 2.486 2.499C17 15.887 16.035 17 14.485 17zM36 36h-5v-9.099c0-2.198-1.225-3.698-3.192-3.698-1.501 0-2.313 1.012-2.707 1.99C24.957 25.543 25 26.511 25 27v9h-5V19h5v2.616C25.721 20.5 26.85 19 29.738 19c3.578 0 6.261 2.25 6.261 7.274L36 36 36 36z"
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div data-supertokens="providerButtonText">
                      Continue with LinkedIn
                    </div>
                  </button>
                </span>
              </div>
            ),
          },
        ],
      },
    }),
    Session.init({
      tokenTransferMethod: "header",
    }),
    EmailPassword.init({
      resetPasswordUsingTokenFeature: {
        style: `
          [data-supertokens~=link] {
            background-color: rgb(2, 132, 199);
            color: white; /* Ensure text is visible */
            padding: 10px 20px; /* Add some padding for better button appearance */
            text-align: center;
            display: inline-block; /* Ensures it behaves like a button */
            border-radius: 6px; /* Rounded corners */
            text-decoration: none; /* Removes underline */
            font-weight: bold;
          }
        `,
      },
      signInAndUpFeature: {
        signUpForm: {
          formFields: [
            { id: "email", label: "Email", placeholder: "Email" },
            { id: "password", label: "Password", placeholder: "Password" },
            {
              id: "given_name",
              label: "First name",
              placeholder: "First name",
            },
            {
              id: "family_name",
              label: "Last name",
              placeholder: "Last name",
            },
          ],
        },
      },
    }),
    EmailVerification.init({
      mode: "REQUIRED",
      style: `
          [data-supertokens~=link] {
            background-color: rgb(2, 132, 199);
            color: white; /* Ensure text is visible */
            padding: 10px 20px; /* Add some padding for better button appearance */
            text-align: center;
            display: inline-block; /* Ensures it behaves like a button */
            border-radius: 6px; /* Rounded corners */
            text-decoration: none; /* Removes underline */
            font-weight: bold;
          }
        `,
    }),
  ],
});

ReactDOM.createRoot(document.getElementById("root")).render(
  // <React.StrictMode>
  <BrowserRouter>
    <SuperTokensWrapper>
      <AuthRecipeComponentsOverrideContextProvider
        components={CustomAuthComponents}
      >
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </PostHogProvider>
      </AuthRecipeComponentsOverrideContextProvider>
    </SuperTokensWrapper>
  </BrowserRouter>
  // </React.StrictMode>
);
